// Centralized pricing configuration
// This eliminates duplication across PricingPage, UpgradeModal, CompanyAdminPage, etc.

export const PLANS = {
  FREE: 'free',
  FOUNDER: 'founder',
  PRO: 'pro',
} as const;

export type PlanType = (typeof PLANS)[keyof typeof PLANS];

export interface PlanConfig {
  id: PlanType;
  name: string;
  tagline: string;
  description: string;
  price: {
    standard: {
      amount: number;
      currency: string;
      interval: 'month' | 'year';
      intervalCount: number;
    };
    launch?: {
      amount: number;
      currency: string;
      interval: 'month' | 'year';
      intervalCount: number;
      disclaimer: string;
    };
  };
  limits: {
    users: number; // -1 for unlimited
    companies: number; // -1 for unlimited
  };
  features: {
    included: string[];
    excluded: string[];
  };
  cta: {
    primary: string;
    secondary?: string;
  };
  requiresPlan?: PlanType; // For Pro requiring Founder
}

export const PRICING_CONFIG: Record<PlanType, PlanConfig> = {
  [PLANS.FREE]: {
    id: PLANS.FREE,
    name: 'Free',
    tagline: 'No longer available',
    description: 'Existing free users will be grandfathered in.',
    price: {
      standard: {
        amount: 0,
        currency: 'USD',
        interval: 'month',
        intervalCount: 1,
      },
    },
    limits: {
      users: -1,
      companies: -1,
    },
    features: {
      included: [],
      excluded: [],
    },
    cta: {
      primary: 'Not Available',
    },
  },

  [PLANS.FOUNDER]: {
    id: PLANS.FOUNDER,
    name: 'Founder',
    tagline: "Design your company's strategy.",
    description: 'Strategic clarity layer for founders and executives.',
    price: {
      standard: {
        amount: 997,
        currency: 'USD',
        interval: 'year',
        intervalCount: 1,
      },
      launch: {
        amount: 197,
        currency: 'USD',
        interval: 'year',
        intervalCount: 1,
        disclaimer: 'Launch pricing valid for first year only. Renews at $997/year.',
      },
    },
    limits: {
      users: 1,
      companies: 1,
    },
    features: {
      included: [
        '1 company',
        '1 user',
        'Vision, Mission, Values',
        'Strategic Objectives',
        'External Analysis (PESTEL, Five Forces, Competitors)',
        'Internal Analysis (SWOT, Success Factors)',
        'Strategy Formulation & Plan Summary',
        'Visual Org Chart',
      ],
      excluded: ['No execution engine', 'No task delegation', 'No AI automation'],
    },
    cta: {
      primary: 'Get Started',
      secondary: 'Claim Launch Pricing',
    },
  },

  [PLANS.PRO]: {
    id: PLANS.PRO,
    name: 'Pro Activation',
    tagline: 'Activate execution across your leadership team.',
    description: 'Full execution engine with AI-powered strategy and accountability.',
    price: {
      standard: {
        amount: 100, // Per user per month
        currency: 'USD',
        interval: 'month',
        intervalCount: 1,
      },
      launch: {
        amount: 20, // Per user per month (launch pricing)
        currency: 'USD',
        interval: 'month',
        intervalCount: 1,
        disclaimer: 'Launch pricing valid for first year only. Renews at $100/user/month.',
      },
    },
    limits: {
      users: 5, // Minimum 5 users required
      companies: 1,
    },
    features: {
      included: [
        'AI strategy generation and diagnostics',
        'Execution Hub: Objectives → Strategies → Projects → Tasks',
        'Task delegation and accountability tracking',
        'Role-based visibility',
        'KPI tracking and execution monitoring',
        'Priority support',
      ],
      excluded: [],
    },
    cta: {
      primary: 'Activate Pro',
      secondary: 'Claim Launch Pricing',
    },
    requiresPlan: PLANS.FOUNDER,
  },
};

// Pricing for annual Pro Activation option (per user)
export const PRO_ANNUAL_PRICE_PER_USER = {
  standard: 1000, // Per user per year
  launch: 240, // Per user per year (launch pricing)
};

// Minimum users for Pro Activation
export const PRO_MIN_USERS = 5;

// Helper functions
export function getPlanConfig(planType: PlanType): PlanConfig {
  return PRICING_CONFIG[planType];
}

export function getPlanPrice(planType: PlanType, isLaunchPricing: boolean = false): number {
  const config = PRICING_CONFIG[planType];
  if (isLaunchPricing && config.price.launch) {
    return config.price.launch.amount;
  }
  return config.price.standard.amount;
}

export function formatPrice(
  amount: number,
  interval: 'month' | 'year',
  currency: string = 'USD'
): string {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return `${formattedAmount}/${interval}`;
}

export function getPlanRequirement(planType: PlanType): PlanType | undefined {
  return PRICING_CONFIG[planType].requiresPlan;
}

export function canAccessPlan(currentPlan: PlanType, targetPlan: PlanType): boolean {
  const requirement = getPlanRequirement(targetPlan);
  if (!requirement) return true;

  // To access Pro, you need Founder
  if (targetPlan === PLANS.PRO) {
    return currentPlan === PLANS.FOUNDER || currentPlan === PLANS.PRO;
  }

  return true;
}
